import { Component, EventEmitter, Input, Output } from "@angular/core";

import {
  ONBOARDING_TRACKED_STEPS,
  OnboardingProgressService,
} from "src/app/core/core-services/services/onboarding-progress.service";

/**
 * The welcome-modal's chrome: the "Getting Started" bar (icon, title, close
 * button, live progress bar) and the "Onboarding" sub-header with its
 * completed/total fraction.
 *
 * Issue #523's enabling refactor: this markup used to be copy-pasted 9x, once
 * per `welcome-modal.component.html` `*ngSwitchCase`, reading the (stale,
 * constructor-seeded) `progressValue` field. It now reads
 * `OnboardingProgressService` directly, so every instance re-renders live the
 * moment ANY step completes — including the "Resume/Retake Onboarding"
 * buttons this refactor removed, which used to sit here (they navigated into
 * the full-page onboarding wizard being retired by issue #524).
 *
 * `:host { display: contents }` (see the stylesheet) lets this component's
 * three root elements (`<header>`, `<hr>`, the sub-header `<div>`) lay out as
 * direct children of the parent's flex column, exactly as the inlined markup
 * used to.
 */
@Component({
    selector: "app-walk-me-header",
    templateUrl: "./walk-me-header.component.html",
    styleUrls: ["./walk-me-header.component.scss"],
    standalone: false
})
export class WalkMeHeaderComponent {
  /**
   * WelcomeModalComponent's current panel index (0-8, or 9 for "more
   * learning videos"). Only used to keep the close button's Heap analytics id
   * exact (`walk-me-modal-close-step-<n>`) — AGENTS.md: "Keep data-fms-heap
   * prefixes exact, Heap analytics depends on them."
   */
  @Input() step: number;
  @Output() readonly closeRequested = new EventEmitter<void>();

  readonly trackedStepCount = ONBOARDING_TRACKED_STEPS.length;

  constructor(public readonly onboardingProgress: OnboardingProgressService) {}

  get closeHeapId(): string {
    return `walk-me-modal-close-step-${this.step === 9 ? "more-learning-videos" : this.step}`;
  }

  get completedCount(): number {
    return this.onboardingProgress.completed.size;
  }

  /**
   * Issue #520: the X only renders once both required steps (install
   * the extension, about you) are done — read live off the service so it
   * appears the instant the second one completes, without the modal being
   * reopened. Backdrop/ESC dismissal is gated separately, in
   * GlobalOverlayComponent.startModal() (DialogService.setDisableClose) —
   * this getter only controls the button's own presence in the DOM.
   */
  get requiredComplete(): boolean {
    return this.onboardingProgress.requiredComplete;
  }
}
